import {
  FileText,
  FileCode,
  FileJson,
  Braces,
  FileType,
  FileImage,
  FileSpreadsheet,
  FileArchive,
  Terminal,
  Database,
  Settings,
  Package,
  Lock,
  Hash,
} from "lucide-react";

interface Props {
  path: string;
  size?: number;
  className?: string;
}

// Visual cue to make file tabs and tree rows easier to scan at a glance.
// We deliberately do NOT try to be exhaustive — these icons are decoration,
// not metadata. The fallback FileText is good enough for anything we miss.
//
// Color hints are inline so a future ext doesn't have to ship matching CSS
// rules; if more nuance is wanted later, lift the colors into CSS vars.
export function FileTypeIcon({ path, size = 13, className }: Props) {
  const base = path.split("/").pop()?.toLowerCase() || "";
  const ext = base.includes(".") ? base.slice(base.lastIndexOf(".") + 1) : base;

  if (base === "dockerfile") return <Package size={size} className={className} color="#0db7ed" />;
  if (base === "makefile") return <Terminal size={size} className={className} color="#999999" />;
  if (base === ".env" || base.startsWith(".env."))
    return <Lock size={size} className={className} color="#facc15" />;
  if (base === ".gitignore" || base === ".gitattributes")
    return <Hash size={size} className={className} color="#f05033" />;
  if (base === "package.json" || base === "package-lock.json")
    return <Package size={size} className={className} color="#cb3837" />;
  if (base === "cargo.toml" || base === "cargo.lock")
    return <Package size={size} className={className} color="#dea584" />;

  switch (ext) {
    case "js":
    case "mjs":
    case "cjs":
    case "jsx":
      return <FileCode size={size} className={className} color="#f7df1e" />;
    case "ts":
    case "tsx":
      return <FileCode size={size} className={className} color="#3178c6" />;
    case "json":
      return <FileJson size={size} className={className} color="#facc15" />;
    case "html":
    case "htm":
      return <FileCode size={size} className={className} color="#e34c26" />;
    case "css":
    case "scss":
    case "less":
      return <FileCode size={size} className={className} color="#264de4" />;
    case "py":
      return <FileCode size={size} className={className} color="#3572a5" />;
    case "rs":
      return <FileCode size={size} className={className} color="#dea584" />;
    case "go":
      return <FileCode size={size} className={className} color="#00add8" />;
    case "java":
      return <FileCode size={size} className={className} color="#b07219" />;
    case "kt":
    case "kts":
      return <FileCode size={size} className={className} color="#a97bff" />;
    case "swift":
      return <FileCode size={size} className={className} color="#f05138" />;
    case "rb":
      return <FileCode size={size} className={className} color="#cc342d" />;
    case "c":
    case "h":
      return <FileCode size={size} className={className} color="#a8b9cc" />;
    case "cpp":
    case "cc":
    case "cxx":
    case "hpp":
    case "hh":
      return <FileCode size={size} className={className} color="#00599c" />;
    case "cs":
      return <FileCode size={size} className={className} color="#9b4f96" />;
    case "lua":
      return <FileCode size={size} className={className} color="#7cc3de" />;
    case "sh":
    case "bash":
    case "zsh":
    case "ps1":
    case "psm1":
      return <Terminal size={size} className={className} color="#4eaa25" />;
    case "yml":
    case "yaml":
    case "toml":
    case "ini":
    case "conf":
    case "properties":
    case "env":
      return <Settings size={size} className={className} color="#999999" />;
    case "xml":
    case "svg":
      return <Braces size={size} className={className} color="#ff6600" />;
    case "md":
    case "markdown":
    case "txt":
      return <FileText size={size} className={className} color="#a0a0a0" />;
    case "sql":
      return <Database size={size} className={className} color="#336791" />;
    case "png":
    case "jpg":
    case "jpeg":
    case "gif":
    case "webp":
    case "ico":
    case "bmp":
      return <FileImage size={size} className={className} color="#a78bfa" />;
    case "csv":
    case "xlsx":
    case "xls":
    case "tsv":
      return <FileSpreadsheet size={size} className={className} color="#22c55e" />;
    case "zip":
    case "tar":
    case "gz":
    case "tgz":
    case "bz2":
    case "7z":
    case "rar":
      return <FileArchive size={size} className={className} color="#a0a0a0" />;
    case "pdf":
      return <FileType size={size} className={className} color="#ef4444" />;
    default:
      return <FileText size={size} className={className} color="#7c8497" />;
  }
}
